// @yawi/types - Integration Types for Admin Panel
// Types for managing third-party integrations with fallback support

// ============ INTEGRATION TYPES ============
export type IntegrationType = 'EMAIL' | 'SMS' | 'WHATSAPP' | 'PUSH' | 'PAYMENT' | 'ACCESS' | 'STORAGE';

export type IntegrationStatus = 'ENABLED' | 'DISABLED' | 'ERROR';

// Email Providers
export type EmailProvider = 'SENDGRID' | 'BREVO' | 'MAILERSEND' | 'SMTP';

// SMS Providers
export type SmsProvider = 'TWILIO';

// WhatsApp Providers
export type WhatsAppProvider = 'META_WHATSAPP' | 'TWILIO_WHATSAPP';

// Push Providers
export type PushProvider = 'FIREBASE';

// Payment Providers
export type PaymentProvider = 'STRIPE' | 'MERCADOPAGO' | 'CONEKTA';

// Access Providers
export type AccessProvider = 'HIKCENTRAL' | 'DAHUA' | 'ZKTECO';

// Storage Providers
export type StorageProvider = 'AWS_S3';

export type IntegrationProvider =
  | EmailProvider
  | SmsProvider
  | WhatsAppProvider
  | PushProvider
  | PaymentProvider
  | AccessProvider
  | StorageProvider;

// ============ INTEGRATION ENTITY ============
export interface Integration {
  id: string;
  type: IntegrationType;
  provider: IntegrationProvider;
  name: string;
  status: IntegrationStatus;
  priority: number;
  lastError: string | null;
  lastUsedAt: string | null;
  errorCount: number;
  createdAt: string;
  updatedAt: string;
  config?: ProviderConfig; // Only included when fetching by ID
}

// ============ PROVIDER CONFIGS ============
export type ProviderConfig =
  | SendGridConfig
  | BrevoConfig
  | MailerSendConfig
  | SmtpConfig
  | TwilioSmsConfig
  | MetaWhatsAppConfig
  | TwilioWhatsAppConfig
  | FirebaseConfig
  | StripeConfig
  | MercadoPagoConfig
  | ConektaConfig;

// Email Configs
export interface SendGridConfig {
  apiKey: string;
  fromEmail: string;
  fromName: string;
}

export interface BrevoConfig {
  apiKey: string;
  fromEmail: string;
  fromName: string;
}

export interface MailerSendConfig {
  apiKey: string;
  fromEmail: string;
  fromName: string;
}

export interface SmtpConfig {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  fromEmail: string;
  fromName: string;
}

// SMS Config
export interface TwilioSmsConfig {
  accountSid: string;
  authToken: string;
  fromNumber: string;
}

// WhatsApp Configs
export interface MetaWhatsAppConfig {
  apiToken: string;
  phoneNumberId: string;
  apiVersion: string;
  verifyToken: string;
  appSecret: string;
}

export interface TwilioWhatsAppConfig {
  accountSid: string;
  authToken: string;
  fromNumber: string;
}

// Push Config
export interface FirebaseConfig {
  projectId: string;
  privateKey: string;
  clientEmail: string;
}

// Payment Configs
export interface StripeConfig {
  secretKey: string;
  publishableKey: string;
  webhookSecret: string;
}

export interface MercadoPagoConfig {
  accessToken: string;
  publicKey: string;
}

export interface ConektaConfig {
  apiKey: string;
  publicKey: string;
}

// ============ DTOs ============
export interface CreateIntegrationDto {
  type: IntegrationType;
  provider: IntegrationProvider;
  name: string;
  config: ProviderConfig;
  priority?: number;
}

export interface UpdateIntegrationDto {
  name?: string;
  config?: Partial<ProviderConfig>;
  priority?: number;
}

// ============ INTEGRATION CATEGORY ============
export type IntegrationCategory = 'ACCESS_SECURITY' | 'ONLINE_PAYMENTS' | 'CLOUD_SERVICES' | 'COMMUNICATION';

// ============ TEST RESULTS ============
export interface IntegrationTestResult {
  success: boolean;
  error?: string;
  responseTimeMs?: number;
}

// ============ PROVIDERS BY TYPE ============
export const PROVIDERS_BY_TYPE: Record<IntegrationType, IntegrationProvider[]> = {
  EMAIL: ['SENDGRID', 'BREVO', 'MAILERSEND', 'SMTP'],
  SMS: ['TWILIO'],
  WHATSAPP: ['META_WHATSAPP', 'TWILIO_WHATSAPP'],
  PUSH: ['FIREBASE'],
  PAYMENT: ['STRIPE', 'MERCADOPAGO', 'CONEKTA'],
  ACCESS: ['HIKCENTRAL', 'DAHUA', 'ZKTECO'],
  STORAGE: ['AWS_S3'],
};

// ============ PROVIDER METADATA ============
export const EMAIL_PROVIDERS: { provider: EmailProvider; name: string; description: string }[] = [
  { provider: 'SENDGRID', name: 'SendGrid', description: 'SendGrid API - Escalable y confiable' },
  { provider: 'BREVO', name: 'Brevo', description: 'Brevo (ex Sendinblue) - Marketing y transaccional' },
  { provider: 'MAILERSEND', name: 'MailerSend', description: 'MailerSend - Simple y económico' },
  { provider: 'SMTP', name: 'SMTP', description: 'SMTP genérico - Compatible con cualquier servidor' },
];

export const SMS_PROVIDERS: { provider: SmsProvider; name: string; description: string }[] = [
  { provider: 'TWILIO', name: 'Twilio SMS', description: 'Twilio - Líder en comunicaciones' },
];

export const WHATSAPP_PROVIDERS: { provider: WhatsAppProvider; name: string; description: string }[] = [
  { provider: 'META_WHATSAPP', name: 'Meta Cloud API', description: 'API oficial de WhatsApp Business' },
  { provider: 'TWILIO_WHATSAPP', name: 'Twilio WhatsApp', description: 'WhatsApp via Twilio' },
];

export const PUSH_PROVIDERS: { provider: PushProvider; name: string; description: string }[] = [
  { provider: 'FIREBASE', name: 'Firebase', description: 'Firebase Cloud Messaging (FCM)' },
];

export const PAYMENT_PROVIDERS: { provider: PaymentProvider; name: string; description: string }[] = [
  { provider: 'STRIPE', name: 'Stripe', description: 'Stripe - Pagos globales' },
  { provider: 'MERCADOPAGO', name: 'MercadoPago', description: 'MercadoPago - LATAM' },
  { provider: 'CONEKTA', name: 'Conekta', description: 'Conekta - México' },
];

export const ACCESS_PROVIDERS: { provider: AccessProvider; name: string; description: string }[] = [
  { provider: 'HIKCENTRAL', name: 'HikCentral', description: 'Hikvision - Control de acceso y CCTV' },
  { provider: 'DAHUA', name: 'Dahua DSS', description: 'Dahua - Videovigilancia y control de acceso' },
  { provider: 'ZKTECO', name: 'ZKTeco', description: 'ZKTeco - Control biométrico' },
];

export const STORAGE_PROVIDERS: { provider: StorageProvider; name: string; description: string }[] = [
  { provider: 'AWS_S3', name: 'Amazon S3', description: 'AWS S3 - Almacenamiento en la nube' },
];

// Provider config field definitions for dynamic forms
export interface ConfigFieldDefinition {
  key: string;
  label: string;
  type: 'text' | 'password' | 'number' | 'boolean' | 'url' | 'email' | 'select';
  placeholder: string;
  required: boolean;
  sensitive?: boolean;
  helpText?: string;
  options?: string[];
}

export const PROVIDER_CONFIG_FIELDS: Record<IntegrationProvider, ConfigFieldDefinition[]> = {
  // Email Providers
  SENDGRID: [
    { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'SG.xxxxxx', required: true },
    { key: 'fromEmail', label: 'From Email', type: 'text', placeholder: 'noreply@tudominio.com', required: true },
    { key: 'fromName', label: 'From Name', type: 'text', placeholder: 'Tu App', required: true },
  ],
  BREVO: [
    { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'xkeysib-xxxxxx', required: true },
    { key: 'fromEmail', label: 'From Email', type: 'text', placeholder: 'noreply@tudominio.com', required: true },
    { key: 'fromName', label: 'From Name', type: 'text', placeholder: 'Tu App', required: true },
  ],
  MAILERSEND: [
    { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'mlsn.xxxxxx', required: true },
    { key: 'fromEmail', label: 'From Email', type: 'text', placeholder: 'noreply@tudominio.com', required: true },
    { key: 'fromName', label: 'From Name', type: 'text', placeholder: 'Tu App', required: true },
  ],
  SMTP: [
    { key: 'host', label: 'SMTP Host', type: 'text', placeholder: 'smtp.gmail.com', required: true },
    { key: 'port', label: 'Port', type: 'number', placeholder: '587', required: true },
    { key: 'secure', label: 'SSL/TLS', type: 'boolean', placeholder: '', required: true },
    { key: 'user', label: 'Username', type: 'text', placeholder: 'tu-usuario@gmail.com', required: true },
    { key: 'password', label: 'Password', type: 'password', placeholder: 'tu-app-password', required: true },
    { key: 'fromEmail', label: 'From Email', type: 'text', placeholder: 'noreply@tudominio.com', required: true },
    { key: 'fromName', label: 'From Name', type: 'text', placeholder: 'Tu App', required: true },
  ],
  // SMS Provider
  TWILIO: [
    { key: 'accountSid', label: 'Account SID', type: 'text', placeholder: 'ACxxxxxx', required: true },
    { key: 'authToken', label: 'Auth Token', type: 'password', placeholder: 'Tu auth token', required: true },
    { key: 'fromNumber', label: 'From Number', type: 'text', placeholder: '+1234567890', required: true },
  ],
  // WhatsApp Providers
  META_WHATSAPP: [
    { key: 'apiToken', label: 'API Token', type: 'password', placeholder: 'EAAxxxxxxxx', required: true },
    { key: 'phoneNumberId', label: 'Phone Number ID', type: 'text', placeholder: '1234567890', required: true },
    { key: 'apiVersion', label: 'API Version', type: 'text', placeholder: 'v18.0', required: true },
    { key: 'verifyToken', label: 'Verify Token', type: 'password', placeholder: 'my-verify-token', required: true },
    { key: 'appSecret', label: 'App Secret', type: 'password', placeholder: 'Tu App Secret', required: true },
  ],
  TWILIO_WHATSAPP: [
    { key: 'accountSid', label: 'Account SID', type: 'text', placeholder: 'ACxxxxxx', required: true },
    { key: 'authToken', label: 'Auth Token', type: 'password', placeholder: 'Tu auth token', required: true },
    { key: 'fromNumber', label: 'From Number', type: 'text', placeholder: '+14155238886', required: true },
  ],
  // Push Provider
  FIREBASE: [
    { key: 'projectId', label: 'Project ID', type: 'text', placeholder: 'my-project-id', required: true },
    { key: 'clientEmail', label: 'Client Email', type: 'text', placeholder: 'service@project.iam.gserviceaccount.com', required: true },
    { key: 'privateKey', label: 'Private Key', type: 'password', placeholder: '-----BEGIN PRIVATE KEY-----...', required: true },
  ],
  // Payment Providers
  STRIPE: [
    { key: 'secretKey', label: 'Secret Key', type: 'password', placeholder: 'sk_live_xxxxxxxx', required: true },
    { key: 'publishableKey', label: 'Publishable Key', type: 'text', placeholder: 'pk_live_xxxxxxxx', required: true },
    { key: 'webhookSecret', label: 'Webhook Secret', type: 'password', placeholder: 'whsec_xxxxxxxx', required: true },
  ],
  MERCADOPAGO: [
    { key: 'accessToken', label: 'Access Token', type: 'password', placeholder: 'APP_USR-xxxxxxxx', required: true },
    { key: 'publicKey', label: 'Public Key', type: 'text', placeholder: 'APP_USR-xxxxxxxx', required: true },
  ],
  CONEKTA: [
    { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'key_xxxxxxxx', required: true },
    { key: 'publicKey', label: 'Public Key', type: 'text', placeholder: 'key_xxxxxxxx', required: true },
  ],
  // Access Providers
  HIKCENTRAL: [
    { key: 'serverUrl', label: 'Server URL', type: 'url', placeholder: 'https://hikcentral.example.com', required: true },
    { key: 'apiKey', label: 'API Key', type: 'text', placeholder: 'Your API key', required: true },
    { key: 'secretKey', label: 'Secret Key', type: 'password', placeholder: 'Your secret key', required: true, sensitive: true },
  ],
  DAHUA: [
    { key: 'serverUrl', label: 'Server URL', type: 'url', placeholder: 'https://dahua.example.com', required: true },
    { key: 'username', label: 'Username', type: 'text', placeholder: 'admin', required: true },
    { key: 'password', label: 'Password', type: 'password', placeholder: 'Your password', required: true, sensitive: true },
  ],
  ZKTECO: [
    { key: 'serverUrl', label: 'Server URL', type: 'url', placeholder: 'https://zkteco.example.com', required: true },
    { key: 'apiKey', label: 'API Key', type: 'text', placeholder: 'Your API key', required: true },
  ],
  // Storage Providers
  AWS_S3: [
    { key: 'bucketName', label: 'Bucket Name', type: 'text', placeholder: 'my-bucket', required: true },
    { key: 'region', label: 'Region', type: 'text', placeholder: 'us-east-1', required: true },
    { key: 'accessKeyId', label: 'Access Key ID', type: 'text', placeholder: 'AKIA...', required: true },
    { key: 'secretAccessKey', label: 'Secret Access Key', type: 'password', placeholder: 'Your secret key', required: true, sensitive: true },
  ],
};

// ============ INTEGRATION LOGS ============
export type IntegrationLogType = 'EMAIL' | 'SMS' | 'WHATSAPP' | 'PUSH' | 'PAYMENT';
export type IntegrationLogStatus = 'SUCCESS' | 'FAILURE' | 'PENDING';

export interface IntegrationLog {
  id: string;
  integrationId: string;
  type: IntegrationLogType;
  provider: string;
  status: IntegrationLogStatus;
  recipient?: string;
  subject?: string;
  amount?: number;
  currency?: string;
  errorMessage?: string;
  requestPayload?: Record<string, unknown>;
  responsePayload?: Record<string, unknown>;
  latencyMs?: number;
  createdAt: string;
}

export interface IntegrationLogFilters {
  type?: IntegrationLogType;
  status?: IntegrationLogStatus;
  integrationId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

// ============ WEBHOOKS ============
export type WebhookStatus = 'ACTIVE' | 'INACTIVE' | 'FAILED';
export type WebhookDeliveryStatus = 'PENDING' | 'SUCCESS' | 'FAILED' | 'RETRYING';

export interface WebhookConfig {
  id: string;
  url: string;
  name: string | null;
  description: string | null;
  events: string[];
  status: WebhookStatus;
  failureCount: number;
  lastTriggeredAt: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  event: string;
  payload: Record<string, unknown>;
  status: WebhookDeliveryStatus;
  responseStatus: number | null;
  attemptCount: number;
  deliveredAt: string | null;
  createdAt: string;
}

export interface CreateWebhookDto {
  url: string;
  name?: string;
  description?: string;
  events: string[];
  secret: string; // Requerido, mínimo 16 caracteres
}

export interface UpdateWebhookDto {
  url?: string;
  name?: string;
  description?: string;
  events?: string[];
  status?: WebhookStatus;
  secret?: string;
}

// ============ AVAILABLE WEBHOOK EVENTS ============
export const WEBHOOK_EVENTS = [
  // Access events
  'access.granted',
  'access.denied',
  // Payment events
  'payment.created',
  'payment.paid',
  'payment.overdue',
  'payment.verified',
  'payment.rejected',
  // Ticket events
  'ticket.created',
  'ticket.updated',
  'ticket.resolved',
  // Reservation events
  'reservation.created',
  'reservation.confirmed',
  'reservation.cancelled',
  // Guest events
  'guest.checkin',
  'guest.checkout',
  'guest.invitation.created',
  // User events
  'user.created',
  'user.blocked',
  'user.unblocked',
  // Announcement events
  'announcement.created',
  // Device events
  'device.online',
  'device.offline',
] as const;

export type WebhookEvent = typeof WEBHOOK_EVENTS[number];

// Webhook events grouped by category for UI
export const WEBHOOK_EVENT_CATEGORIES = {
  access: [
    { value: 'access.granted', label: 'Acceso concedido' },
    { value: 'access.denied', label: 'Acceso denegado' },
  ],
  payments: [
    { value: 'payment.created', label: 'Pago creado' },
    { value: 'payment.paid', label: 'Pago realizado' },
    { value: 'payment.overdue', label: 'Pago vencido' },
    { value: 'payment.verified', label: 'Pago verificado' },
    { value: 'payment.rejected', label: 'Pago rechazado' },
  ],
  tickets: [
    { value: 'ticket.created', label: 'Ticket creado' },
    { value: 'ticket.updated', label: 'Ticket actualizado' },
    { value: 'ticket.resolved', label: 'Ticket resuelto' },
  ],
  reservations: [
    { value: 'reservation.created', label: 'Reservación creada' },
    { value: 'reservation.confirmed', label: 'Reservación confirmada' },
    { value: 'reservation.cancelled', label: 'Reservación cancelada' },
  ],
  guests: [
    { value: 'guest.checkin', label: 'Check-in de invitado' },
    { value: 'guest.checkout', label: 'Check-out de invitado' },
    { value: 'guest.invitation.created', label: 'Invitación creada' },
  ],
  users: [
    { value: 'user.created', label: 'Usuario creado' },
    { value: 'user.blocked', label: 'Usuario bloqueado' },
    { value: 'user.unblocked', label: 'Usuario desbloqueado' },
  ],
  announcements: [
    { value: 'announcement.created', label: 'Anuncio creado' },
  ],
  devices: [
    { value: 'device.online', label: 'Dispositivo en línea' },
    { value: 'device.offline', label: 'Dispositivo fuera de línea' },
  ],
} as const;
